import TimelineIcon from '@mui/icons-material/EventNote'
import GroupsIcon from '@mui/icons-material/Groups'
import StatisticsIcon from '@mui/icons-material/Insights'
import DefaultIcon from '@mui/icons-material/ViewList'
import { Badge, Box } from '@mui/material'
import { useTranslate } from 'ra-core'
import { Fragment } from 'react'
import { MenuItemLink, usePermissions, useResourceDefinitions, useSidebarState } from 'react-admin'

import { useAccounts } from '../api/accountsProvider'
import { SYSTEM_PERMISSION_LIST, SYSTEM_PERMISSION_READ, UI_PERMISSION_MENU } from '../config/permissions'
import { hasResourcePermission, useResourcePermissions } from '../domain/permissions'
import { AccessGroupsPage } from '../resources/accessGroups/page'
import { STATISTICS_KEY, StatisticsPage } from '../resources/statistics/page'
import vehiclesConfig from '../resources/vehicles/config'
import { TIMELINE_KEY, VehiclesTimelinePage } from '../resources/vehicles/timeline'

const Menu = () => {
  const { currentAccount } = useAccounts()
  const [sidebarIsOpen] = useSidebarState()
  const translate = useTranslate()

  const resourcesDefinitions = useResourceDefinitions()
  const resources = Object.keys(resourcesDefinitions).map((name) => resourcesDefinitions[name])
  const { permissions } = usePermissions()
  const displayedResources = resources.filter((resource) =>
    hasResourcePermission(permissions, resource.name, SYSTEM_PERMISSION_READ, UI_PERMISSION_MENU),
  )
  const [hasReadForVehicles] = useResourcePermissions(vehiclesConfig.name, SYSTEM_PERMISSION_READ)
  const hasStatisticsMenu = hasResourcePermission(
    permissions,
    STATISTICS_KEY,
    SYSTEM_PERMISSION_READ,
    UI_PERMISSION_MENU,
  )
  const hasGroupsMenu = ['user-groups', 'vehicle-groups'].some((name) =>
    hasResourcePermission(permissions, name, SYSTEM_PERMISSION_READ, SYSTEM_PERMISSION_LIST, UI_PERMISSION_MENU),
  )
  const menuResources = displayedResources.filter(
    (resource) => !['user-groups', 'vehicle-groups'].includes(resource.name),
  )
  const groupsMenuAfter = ['vehicles', 'users'].find((name) => menuResources.some((resource) => resource.name === name))

  const getMenuItemLinkCommonProps = (name) => ({
    primaryText: sidebarIsOpen ? name : '',
    sx: { marginBottom: '5px', paddingRight: sidebarIsOpen ? undefined : 0 },
    tooltipProps: { title: name },
  })
  const groupsMenuItem = hasGroupsMenu && (
    <MenuItemLink
      {...getMenuItemLinkCommonProps(translate('mymove.accessGroups.title'))}
      leftIcon={<GroupsIcon />}
      to={AccessGroupsPage.path}
    />
  )

  return (
    <Box mt={1}>
      {hasReadForVehicles && (
        <MenuItemLink
          {...getMenuItemLinkCommonProps(translate('mymove.scheduler.name'))}
          key={TIMELINE_KEY}
          leftIcon={<TimelineIcon />}
          to={VehiclesTimelinePage.path}
        />
      )}
      {hasStatisticsMenu && (
        <MenuItemLink
          {...getMenuItemLinkCommonProps(translate('mymove.statistics.name'))}
          key={STATISTICS_KEY}
          leftIcon={<StatisticsIcon />}
          to={StatisticsPage.path}
        />
      )}
      {!groupsMenuAfter && groupsMenuItem}
      {menuResources.map((resource) => {
        let icon = resource.icon ? <resource.icon /> : <DefaultIcon />
        if (currentAccount && resource.options?.badge) {
          const badgeCount =
            currentAccount.counts?.[resource.name] ??
            currentAccount.counts?.[resource.name.replace(/-/g, '_')] ??
            (resource.name === 'incident-reports' ? currentAccount.counts?.['damage-reports'] : undefined)
          icon = (
            <Badge badgeContent={badgeCount} color="error" overlap="rectangular">
              {icon}
            </Badge>
          )
        }
        return (
          <Fragment key={resource.name}>
            <MenuItemLink
              {...getMenuItemLinkCommonProps(translate(`resources.${resource.name}.name`, 2))}
              leftIcon={icon}
              to={`/${resource.name}`}
            />
            {resource.name === groupsMenuAfter && groupsMenuItem}
          </Fragment>
        )
      })}
    </Box>
  )
}

export default Menu
